/**
 * Compre Junto — Web Components
 *
 * <compre-junto-card>     — one product card, handles variant selection + selection state
 * <compre-junto>          — root, aggregates state, runs Swiper, handles batched add-to-cart
 *
 * Communication: card emits `card:state` events (bubbles) with full state snapshot.
 */

// Shared cache across all cards (deduplicates /products/{handle}.js fetches)
if (!window.__compreJuntoCache) {
    window.__compreJuntoCache = new Map();
}

async function fetchProduct(handle) {
    const cache = window.__compreJuntoCache;
    if (cache.has(handle)) return cache.get(handle);

    try {
        const url = `${window.Shopify?.routes?.root || '/'}products/${handle}.js`;
        const res = await fetch(url);
        if (!res.ok) {
            // 404 (definite negative) — cache to avoid refetching nonexistent handles
            cache.set(handle, null);
            return null;
        }
        const product = await res.json();
        cache.set(handle, product);
        return product;
    } catch (e) {
        // Network error — don't cache so retries can recover
        return null;
    }
}

function formatMoney(cents) {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    });
}

if (!customElements.get('compre-junto-card')) {
    class CompreJuntoCard extends HTMLElement {
        constructor() {
            super();
            this.state = {
                selected: false,
                variantId: null,
                price: 0,
                category: null,
                title: '',
                available: false
            };
            this._resolveSeq = 0;
        }

        connectedCallback() {
            this.cardIndex = parseInt(this.dataset.cardIndex, 10);
            this.mode = this.dataset.mode; // 'standard' | 'chinelo'
            this.isCurrent = this.dataset.isCurrent === 'true';
            this.handle = this.dataset.productHandle;
            this.productTitle = this.dataset.productTitle;
            this.basePrice = parseInt(this.dataset.productBasePrice, 10) || 0;
            this._userInteracted = false;

            this.checkbox = this.querySelector('[data-checkbox]');
            this.priceDisplay = this.querySelector('[data-price-display]');

            this.bindCheckbox();

            if (this.mode === 'standard') {
                this.bindStandard();
            } else if (this.mode === 'chinelo') {
                this.bindChinelo();
            }
        }

        bindCheckbox() {
            if (!this.checkbox) return;
            this.checkbox.addEventListener('click', () => {
                if (this.checkbox.disabled) return;
                this.toggleSelected();
            });
        }

        toggleSelected() {
            // Only allow toggling once a variant is resolved
            if (!this.state.variantId || !this.state.available) return;
            this.setSelected(!this.state.selected);
        }

        setSelected(selected) {
            this.state.selected = selected;
            if (this.checkbox) {
                this.checkbox.setAttribute('aria-checked', String(selected));
            }
            this.emitState();
        }

        setCheckboxEnabled(enabled) {
            if (!this.checkbox) return;
            this.checkbox.disabled = !enabled;
        }

        emitState() {
            this.dispatchEvent(new CustomEvent('card:state', {
                bubbles: true,
                detail: {
                    cardIndex: this.cardIndex,
                    selected: this.state.selected,
                    variantId: this.state.variantId,
                    price: this.state.price,
                    category: this.state.category,
                    title: this.state.title,
                    available: this.state.available
                }
            }));
        }

        // ─── STANDARD MODE ─────────────────────────────────────
        bindStandard() {
            this.optionSelects = this.querySelectorAll('[data-option-position]');
            this.optionSelects.forEach((sel) => {
                sel.addEventListener('change', () => {
                    this._userInteracted = true;
                    this.resolveStandard();
                });
            });

            // If has_only_default_variant: no selects, resolve immediately
            if (this.optionSelects.length === 0) {
                this.resolveStandard();
            } else if (this.isCurrent) {
                // Pre-selected options came from Liquid; resolve once
                this.resolveStandard();
            }
        }

        async resolveStandard() {
            const seq = ++this._resolveSeq;
            const product = await fetchProduct(this.handle);
            if (seq !== this._resolveSeq) return;
            if (!product) return;

            let variant;
            if (this.optionSelects.length === 0) {
                variant = product.variants[0];
            } else {
                const selected = Array.from(this.optionSelects).map((s) => s.value);
                // If any option is unselected, we can't resolve yet
                if (selected.some((v) => !v)) {
                    this.state.variantId = null;
                    this.state.available = false;
                    this.setCheckboxEnabled(false);
                    this.setSelected(false);
                    return;
                }
                variant = product.variants.find((v) => {
                    return selected.every((val, i) => v.options[i] === val);
                });
            }

            if (!variant) {
                this.state.variantId = null;
                this.state.available = false;
                this.setCheckboxEnabled(false);
                this.setSelected(false);
                this.updatePriceDisplay(null, false);
                return;
            }

            this.state.variantId = String(variant.id);
            this.state.price = variant.price;
            this.state.available = variant.available;
            this.state.category = 'ready'; // standard products always count as pronta entrega
            this.state.title = this.productTitle;

            this.updatePriceDisplay(variant.price, variant.available);
            this.setCheckboxEnabled(variant.available);

            // Auto-select when: current product on initial load OR user just picked a variant
            if (this.isCurrent || this._userInteracted) {
                this.setSelected(variant.available);
            } else {
                this.emitState();
            }
        }

        updatePriceDisplay(cents, available) {
            if (!this.priceDisplay) return;
            if (cents == null || !available) {
                this.priceDisplay.textContent = 'Indisponível';
                this.priceDisplay.dataset.unavailable = 'true';
                return;
            }
            this.priceDisplay.textContent = formatMoney(cents);
            delete this.priceDisplay.dataset.unavailable;
        }

        // ─── CHINELO MODE ──────────────────────────────────────
        bindChinelo() {
            this.colorSelect = this.querySelector('[data-chinelo-color]');
            this.sizeSelect = this.querySelector('[data-chinelo-size]');
            this.chineloBase = this.dataset.chineloBase || 'classico';
            this.chineloColors = (this.dataset.chineloColors || '')
                .split(',')
                .map((c) => c.trim().toLowerCase())
                .filter(Boolean);
            this.chineloSizes = (this.dataset.chineloSizes || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
            this.chineloMaxLots = parseInt(this.dataset.chineloMaxLots, 10) || 4;
            this.currentColor = this.dataset.chineloCurrentColor || '';

            // Map<colorKey, Map<sizeHandle, {lot, product}>>
            this.matrixCache = new Map();
            this.matrixLoaded = new Set(); // colors that finished loading

            this.colorSelect?.addEventListener('change', () => {
                this._userInteracted = true;
                this.handleChineloColorChange();
            });
            this.sizeSelect?.addEventListener('change', () => {
                this._userInteracted = true;
                this.handleChineloSizeChange();
            });

            if (this.isCurrent) {
                // Pre-load matrix for current color + restore size from URL/sessionStorage
                this.loadMatrixForColor(this.currentColor).then(() => {
                    this.refreshSizeOptions(this.currentColor);

                    const urlParams = new URLSearchParams(window.location.search);
                    const savedSize = urlParams.get('size') || sessionStorage.getItem('lot-selector-size');

                    let target = null;
                    if (savedSize && this.matrixCache.get(this.currentColor)?.has(this.sizeToHandle(savedSize))) {
                        target = savedSize;
                    } else {
                        // First available size
                        const colorMap = this.matrixCache.get(this.currentColor);
                        if (colorMap) {
                            for (const size of this.chineloSizes) {
                                if (colorMap.has(this.sizeToHandle(size))) {
                                    target = size;
                                    break;
                                }
                            }
                        }
                    }
                    if (target && this.sizeSelect) {
                        this.sizeSelect.value = target;
                        this.handleChineloSizeChange();
                    } else {
                        // No available size for current color — mark price unavailable
                        this.updatePriceDisplay(null, false);
                    }
                });
            }
        }

        sizeToHandle(size) {
            return size.replace(/\//g, '-');
        }

        async loadMatrixForColor(color) {
            if (!color) return;
            if (this.matrixLoaded.has(color)) return;

            this.dataset.loading = 'true';

            const colorMap = new Map();
            const tasks = [];

            for (const size of this.chineloSizes) {
                const sizeHandle = this.sizeToHandle(size);
                tasks.push(this.findAvailableLot(color, sizeHandle).then((found) => {
                    if (found) colorMap.set(sizeHandle, found);
                }));
            }

            await Promise.all(tasks);
            this.matrixCache.set(color, colorMap);
            this.matrixLoaded.add(color);

            delete this.dataset.loading;
        }

        async findAvailableLot(color, sizeHandle) {
            for (let lot = 1; lot <= this.chineloMaxLots; lot++) {
                const handle = `${this.chineloBase}-${color}-${sizeHandle}-lt${lot}`;
                const product = await fetchProduct(handle);
                if (product && product.available) {
                    const variant = product.variants?.[0];
                    return {
                        lot,
                        handle,
                        variantId: variant?.id,
                        price: variant?.price || product.price,
                        title: this.stripLotSuffix(product.title),
                        image: product.featured_image,
                        available: true
                    };
                }
            }
            return null;
        }

        // Removes trailing lot + size suffixes from chinelo titles, e.g.
        //   "Classico Amarelo - 33/34 - lote 1" → "Classico Amarelo"
        //   "Classico Azul Lt 2"               → "Classico Azul"
        //   "Classico Verde - 39"              → "Classico Verde"
        stripLotSuffix(title) {
            if (!title) return title;
            let t = title;
            // Run the cleanup repeatedly in case both suffixes are present
            for (let i = 0; i < 3; i++) {
                const before = t;
                // Trailing " - Lote N" / " Lt N" / " LOTE 4" etc.
                t = t.replace(/\s*[-–—]?\s*(lote|lt)\s*\d+\s*$/i, '');
                // Trailing size like " - 33/34" or " - 39"
                t = t.replace(/\s*[-–—]?\s*\d+\s*\/\s*\d+\s*$/, '');
                t = t.replace(/\s*[-–—]\s*\d+\s*$/, '');
                if (t === before) break;
            }
            return t.trim();
        }

        updateChineloMedia(data) {
            if (!data) return;

            const img = this.querySelector('.cj-card__image');
            if (img && data.image) {
                // featured_image from products.js comes as a path like
                // "//cdn.shopify.com/..." — append width param if absent
                const url = data.image.includes('width=')
                    ? data.image
                    : `${data.image}${data.image.includes('?') ? '&' : '?'}width=400`;
                img.src = url;
                img.alt = data.title || '';
            }

            const titleEl = this.querySelector('.cj-card__title');
            if (titleEl && data.title) {
                titleEl.textContent = data.title;
            }
        }

        firstAvailableInColor(color) {
            const colorMap = this.matrixCache.get(color);
            if (!colorMap) return null;
            for (const size of this.chineloSizes) {
                const data = colorMap.get(this.sizeToHandle(size));
                if (data) return data;
            }
            return null;
        }

        refreshSizeOptions(color) {
            if (!this.sizeSelect) return;
            const colorMap = this.matrixCache.get(color);
            const options = this.sizeSelect.querySelectorAll('option[data-size-handle]');
            options.forEach((opt) => {
                const has = colorMap?.has(opt.dataset.sizeHandle);
                opt.disabled = !has;
                if (has) {
                    delete opt.dataset.unavailable;
                } else {
                    opt.dataset.unavailable = 'true';
                }
            });
        }

        async handleChineloColorChange() {
            const color = this.colorSelect?.value;

            // Reset size + selection state (always, including when color cleared)
            if (this.sizeSelect) this.sizeSelect.value = '';
            this.state.variantId = null;
            this.state.available = false;
            this.setCheckboxEnabled(false);
            this.setSelected(false);
            this.updatePriceDisplay(null, false);

            if (!color) return;

            // Race guard: bail if a newer interaction has started
            const seq = ++this._resolveSeq;
            await this.loadMatrixForColor(color);
            if (seq !== this._resolveSeq) return;

            this.refreshSizeOptions(color);

            // Update image + title to reflect the new color (first available size)
            const preview = this.firstAvailableInColor(color);
            if (preview) this.updateChineloMedia(preview);
        }

        async handleChineloSizeChange() {
            const color = this.colorSelect?.value;
            const size = this.sizeSelect?.value;
            if (!color || !size) {
                this.state.variantId = null;
                this.state.available = false;
                this.setCheckboxEnabled(false);
                this.setSelected(false);
                return;
            }

            // Race guard: bail if a newer interaction has started
            const seq = ++this._resolveSeq;

            // Ensure matrix loaded (defensive — first interaction in non-current cards)
            await this.loadMatrixForColor(color);

            if (seq !== this._resolveSeq) return;

            const sizeHandle = this.sizeToHandle(size);
            const data = this.matrixCache.get(color)?.get(sizeHandle);

            if (!data) {
                this.state.variantId = null;
                this.state.available = false;
                this.setCheckboxEnabled(false);
                this.setSelected(false);
                this.updatePriceDisplay(null, false);
                return;
            }

            this.state.variantId = String(data.variantId);
            this.state.price = data.price;
            this.state.available = true;
            this.state.title = data.title;
            // Lot 1-2 are pronta entrega; lot 3+ are lote futuro (matches lot-size-selector convention)
            this.state.category = data.lot <= 2 ? 'ready' : 'lot_special';

            this.updatePriceDisplay(data.price, true);
            this.updateChineloMedia(data);
            this.setCheckboxEnabled(true);
            // Auto-select when: current product on initial load OR user just picked a variant
            if (this.isCurrent || this._userInteracted) {
                this.setSelected(true);
            } else {
                this.emitState();
            }
        }
    }

    customElements.define('compre-junto-card', CompreJuntoCard);
}

if (!customElements.get('compre-junto')) {
    class CompreJunto extends HTMLElement {
        constructor() {
            super();
            this.cardStates = new Map();
            this.swiper = null;
        }

        connectedCallback() {
            this.addCartUrl = this.dataset.addCartUrl || '/cart/add.js';
            this.totalDisplay = this.querySelector('[data-total-display]');
            this.conflictAlert = this.querySelector('[data-conflict]');
            this.addBtn = this.querySelector('[data-add-btn]');
            this.addBtnText = this.querySelector('[data-add-btn-text]');
            this.defaultBtnText = this.addBtnText?.textContent || 'ADICIONAR SELECIONADOS';

            this.bindEvents();
            this.initSwiper();
        }

        bindEvents() {
            this.addEventListener('card:state', (e) => this.handleCardState(e));
            this.addBtn?.addEventListener('click', () => this.handleAddToCart());

            // Re-init Swiper when theme editor selects this block
            document.addEventListener('shopify:block:select', (e) => {
                if (this.contains(e.target) && this.swiper) this.swiper.update();
            });
        }

        initSwiper() {
            if (typeof window.Swiper === 'undefined') return;
            const el = this.querySelector('.compre-junto__swiper');
            if (!el) return;

            const slidesDesktop = parseFloat(this.dataset.slidesDesktop) || 3;
            const slidesMobile = parseFloat(this.dataset.slidesMobile) || 2.2;
            const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 16;
            const showArrows = this.dataset.showArrows === 'true';
            const showPagination = this.dataset.showPagination === 'true';
            const loop = this.dataset.loop === 'true';

            // slidesPerView: 'auto' + explicit slide width via CSS vars avoids
            // Swiper's decimal-slidesPerView math which adds spaceBetween per recalc
            // when inside an align-self:start (intrinsic-width) container.
            this.style.setProperty('--cj-slide-width-mobile', `${(100 / slidesMobile).toFixed(2)}%`);
            this.style.setProperty('--cj-slide-width-desktop', `${(100 / slidesDesktop).toFixed(2)}%`);

            this.swiper = new window.Swiper(el, {
                slidesPerView: 'auto',
                spaceBetween,
                loop,
                watchOverflow: false,
                navigation: showArrows ? {
                    prevEl: el.querySelector('.swiper-button-prev'),
                    nextEl: el.querySelector('.swiper-button-next')
                } : false,
                pagination: showPagination ? {
                    el: el.querySelector('.swiper-pagination'),
                    clickable: true
                } : false
            });
        }

        handleCardState(e) {
            this.cardStates.set(e.detail.cardIndex, e.detail);
            this.recalculate();
        }

        getSelectedStates() {
            return Array.from(this.cardStates.values()).filter((s) => s.selected && s.variantId);
        }

        hasConflict(states) {
            const hasReady = states.some((s) => s.category === 'ready');
            const hasFuture = states.some((s) => s.category === 'lot_special');
            return hasReady && hasFuture;
        }

        recalculate() {
            const selected = this.getSelectedStates();
            const conflict = this.hasConflict(selected);

            // Total
            if (this.totalDisplay) {
                const total = selected.reduce((acc, s) => acc + (s.price || 0), 0);
                this.totalDisplay.textContent = formatMoney(total);
            }

            // Conflict alert
            if (this.conflictAlert) {
                this.conflictAlert.hidden = !conflict;
            }

            // Button
            if (this.addBtn) {
                this.addBtn.disabled = selected.length === 0 || conflict;
            }
        }

        async handleAddToCart() {
            const selected = this.getSelectedStates();
            if (selected.length === 0 || this.hasConflict(selected)) return;

            this.addBtn.disabled = true;
            this.addBtn.dataset.adding = 'true';
            const originalText = this.addBtnText?.textContent;
            if (this.addBtnText) this.addBtnText.textContent = 'Adicionando...';

            // Collect cart section IDs for proper re-rendering
            const cartItems = document.querySelectorAll('cart-items-component');
            const sectionIds = [];
            cartItems.forEach((c) => {
                if (c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
            });

            try {
                const payload = {
                    items: selected.map((s) => ({ id: s.variantId, quantity: 1 }))
                };
                if (sectionIds.length) payload.sections = sectionIds.join(',');

                const res = await fetch(this.addCartUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                const result = await res.json();
                if (result.status || result.errors) {
                    throw new Error(result.description || result.message || 'Erro ao adicionar');
                }

                document.dispatchEvent(new CustomEvent('cart:update', {
                    bubbles: true,
                    detail: {
                        resource: result,
                        sourceId: 'compre-junto',
                        data: {
                            source: 'compre-junto',
                            itemCount: selected.length,
                            sections: result.sections
                        }
                    }
                }));

                // Reset non-current cards to prevent accidental double-add
                this.querySelectorAll('compre-junto-card').forEach((card) => {
                    if (card.dataset.isCurrent !== 'true') {
                        card.setSelected(false);
                    }
                });

                if (this.addBtnText) this.addBtnText.textContent = 'Adicionado!';
                setTimeout(() => {
                    if (this.addBtnText) this.addBtnText.textContent = originalText || this.defaultBtnText;
                }, 1500);

            } catch (err) {
                console.error('[compre-junto] add to cart failed', err);
                if (this.addBtnText) this.addBtnText.textContent = 'Erro — tente novamente';
                setTimeout(() => {
                    if (this.addBtnText) this.addBtnText.textContent = originalText || this.defaultBtnText;
                }, 2000);
            } finally {
                delete this.addBtn.dataset.adding;
                this.recalculate(); // re-enable button based on current state
            }
        }
    }

    customElements.define('compre-junto', CompreJunto);
}
